import type { InsuranceProviderPlanDetailViewModel } from '@generated';
import { editPlanSubmissionSettingsOptions, getInsuranceProviderPlanDetailOptions } from '@generated';
import { Form } from '~/shared/forms/Form';
import { FooterButtonContainer } from '~/shared/forms/buttons/FooterButtonContainer';
import { FormSubmitButton } from '~/shared/forms/buttons/FormSubmitButton';
import { TextFormField } from '~/shared/forms/controls/fields/text-fields/TextFormField';
import { AddressFormField } from '~/shared/forms/controls/fields/AddressFormField';
import { CheckboxFormField } from '~/shared/forms/controls/fields/CheckboxFormField';
import Yup from '~/util/yup';

interface SubmissionSettingsTabProps {
	plan: InsuranceProviderPlanDetailViewModel;
}

export function SubmissionSettingsTab({ plan }: SubmissionSettingsTabProps) {
	const submissionModelId = plan.claimSubmissionModelId;

	return (
		<div className="mt-6">
			<div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
				<h3 className="mb-4 text-lg font-semibold text-gray-900">Submission Settings</h3>
				{!submissionModelId && <p className="text-sm text-gray-500">Please configure a claim submission model on the Billing tab first.</p>}

				{submissionModelId === 1 && <EdiSettingsForm plan={plan} />}
				{submissionModelId === 2 && <PortalSettingsForm plan={plan} />}
				{submissionModelId === 3 && <PaperSettingsForm plan={plan} />}
			</div>
		</div>
	);
}

function EdiSettingsForm({ plan }: SubmissionSettingsTabProps) {
	return (
		<Form
			initialValues={{
				planId: plan.id,
				ediSettings: {
					intermediaryName: plan.ediSettings?.intermediaryName ?? '',
					intermediaryId: plan.ediSettings?.intermediaryId ?? '',
					tradingPartnerId: plan.ediSettings?.tradingPartnerId ?? '',
					submitterId: plan.ediSettings?.submitterId ?? '',
					receiverId: plan.ediSettings?.receiverId ?? ''
				}
			}}
			validationSchema={Yup.object().shape({
				ediSettings: Yup.object().shape({
					intermediaryName: Yup.string().label('Intermediary Name').required(),
					intermediaryId: Yup.string().label('Intermediary ID').nullable(),
					tradingPartnerId: Yup.string().label('Trading Partner ID').required(),
					submitterId: Yup.string().label('Submitter ID').required(),
					receiverId: Yup.string().label('Receiver ID').required()
				})
			})}
			saveMutation={{
				...editPlanSubmissionSettingsOptions,
				invalidateQueryKeys: [getInsuranceProviderPlanDetailOptions(plan.id).queryKey],
				onSuccessMessage: 'EDI settings updated successfully'
			}}
		>
			{formHelpers => (
				<>
					{formHelpers.errorElement}
					<div className="space-y-6">
						<h4 className="font-medium text-gray-700">EDI Configuration</h4>
						<div className="grid grid-cols-2 gap-6">
							<TextFormField name="ediSettings.intermediaryName" inputProps={{ placeholder: 'Intermediary Name' }} />
							<TextFormField name="ediSettings.intermediaryId" inputProps={{ placeholder: 'Intermediary ID' }} />
							<TextFormField name="ediSettings.tradingPartnerId" inputProps={{ placeholder: 'Trading Partner ID' }} />
							<TextFormField name="ediSettings.submitterId" inputProps={{ placeholder: 'Submitter ID' }} />
							<TextFormField name="ediSettings.receiverId" inputProps={{ placeholder: 'Receiver ID' }} />
						</div>
						<FooterButtonContainer className="border-t border-gray-200 pt-4">
							<FormSubmitButton />
						</FooterButtonContainer>
					</div>
				</>
			)}
		</Form>
	);
}

function PortalSettingsForm({ plan }: SubmissionSettingsTabProps) {
	return (
		<Form
			initialValues={{
				planId: plan.id,
				portalSettings: {
					portalUrl: plan.portalSettings?.portalUrl ?? '',
					username: plan.portalSettings?.username ?? '',
					password: plan.portalSettings?.password ?? '',
					portalType: plan.portalSettings?.portalType ?? '',
					sessionTimeout: plan.portalSettings?.sessionTimeout ?? null
				}
			}}
			validationSchema={Yup.object().shape({
				portalSettings: Yup.object().shape({
					portalUrl: Yup.string().label('Portal URL').required(),
					username: Yup.string().label('Username').required(),
					password: Yup.string().label('Password').required(),
					portalType: Yup.string().label('Portal Type').nullable(),
					sessionTimeout: Yup.number().label('Session Timeout').nullable()
				})
			})}
			saveMutation={{
				...editPlanSubmissionSettingsOptions,
				invalidateQueryKeys: [getInsuranceProviderPlanDetailOptions(plan.id).queryKey],
				onSuccessMessage: 'Portal settings updated successfully'
			}}
		>
			{formHelpers => (
				<>
					{formHelpers.errorElement}
					<div className="space-y-6">
						<h4 className="font-medium text-gray-700">Portal Configuration</h4>
						<div className="grid grid-cols-2 gap-6">
							<TextFormField
								name="portalSettings.portalUrl"
								className="col-span-2"
								inputProps={{ placeholder: 'https://portal.example.com' }}
							/>
							<TextFormField name="portalSettings.username" inputProps={{ placeholder: 'Username' }} />
							<TextFormField name="portalSettings.password" inputProps={{ placeholder: 'Password', type: 'password' }} />
							<TextFormField name="portalSettings.portalType" inputProps={{ placeholder: 'Portal Type' }} />
						</div>
						<FooterButtonContainer className="border-t border-gray-200 pt-4">
							<FormSubmitButton />
						</FooterButtonContainer>
					</div>
				</>
			)}
		</Form>
	);
}

function PaperSettingsForm({ plan }: SubmissionSettingsTabProps) {
	return (
		<Form
			initialValues={{
				planId: plan.id,
				paperSubmissionSettings: {
					mailingAddress: plan.paperSubmissionSettings?.mailingAddress ?? null,
					attentionTo: plan.paperSubmissionSettings?.attentionTo ?? '',
					specialInstructions: plan.paperSubmissionSettings?.specialInstructions ?? '',
					submissionDeadlineDays: plan.paperSubmissionSettings?.submissionDeadlineDays ?? null,
					requireCertifiedMail: plan.paperSubmissionSettings?.requireCertifiedMail ?? false
				}
			}}
			validationSchema={Yup.object().shape({
				paperSubmissionSettings: Yup.object().shape({
					mailingAddress: Yup.object().label('Mailing Address').address().required(),
					attentionTo: Yup.string().label('Attention To').nullable(),
					specialInstructions: Yup.string().label('Special Instructions').nullable(),
					submissionDeadlineDays: Yup.number().label('Submission Deadline (Days)').nullable(),
					requireCertifiedMail: Yup.boolean().label('Require Certified Mail').nullable()
				})
			})}
			saveMutation={{
				...editPlanSubmissionSettingsOptions,
				invalidateQueryKeys: [getInsuranceProviderPlanDetailOptions(plan.id).queryKey],
				onSuccessMessage: 'Paper submission settings updated successfully'
			}}
		>
			{formHelpers => (
				<>
					{formHelpers.errorElement}
					<div className="space-y-6">
						<h4 className="font-medium text-gray-700">Paper Submission Configuration</h4>
						<div className="grid grid-cols-2 gap-6">
							<div className="col-span-2">
								<AddressFormField name="paperSubmissionSettings.mailingAddress" />
							</div>
							<TextFormField name="paperSubmissionSettings.attentionTo" inputProps={{ placeholder: 'Attention To' }} />
							<TextFormField
								name="paperSubmissionSettings.submissionDeadlineDays"
								inputProps={{ placeholder: 'Days', type: 'number' }}
							/>
							<TextFormField
								name="paperSubmissionSettings.specialInstructions"
								className="col-span-2"
								inputProps={{ placeholder: 'Special Instructions' }}
							/>
							<CheckboxFormField name="paperSubmissionSettings.requireCertifiedMail" hideLabel />
						</div>
						<FooterButtonContainer className="border-t border-gray-200 pt-4">
							<FormSubmitButton />
						</FooterButtonContainer>
					</div>
				</>
			)}
		</Form>
	);
}
